import { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import { Sparkles, TrendingDown, Wallet } from "lucide-react";
import {
    VOLUME_TIERS,
    VERTICAL_CLASSES,
    calculateMonthly,
} from "../data/fees";
import { useLanguage } from "../contexts/LanguageContext";

const I18N = {
    en: { eyebrow: "Live Calculator", title: "See exactly what you'll pay.", sub: "Drag the slider, pick your vertical, and watch your tier unlock. No subscription — the top-up fee below is the only thing you pay.", vertical: "Vertical class", spend: "Monthly ad spend", perMo: "/mo", yourTier: "Your tier", fee: "Top-up fee", monthlyFee: "Monthly fee", cashback: "Cashback · up to", net: "Net monthly cost", whale: "Whale tier unlocked.", whaleText: "Lowest rates on every standard vertical, plus up to 2% cashback on monthly spend.", tierLabel: null, tierDesc: null },
    tr: { eyebrow: "Canlı hesaplayıcı", title: "Ne ödeyeceğini net gör.", sub: "Kaydırıcıyı sürükle, dikeyini seç, kademen otomatik açılsın. Abonelik yok — aşağıdaki yükleme komisyonu ödediğin tek şey.", vertical: "Dikey sınıfı", spend: "Aylık reklam harcaması", perMo: "/ay", yourTier: "Kademen", fee: "Yükleme komisyonu", monthlyFee: "Aylık komisyon", cashback: "Cashback · %'ye kadar", net: "Net aylık maliyet", whale: "Whale kademesi açıldı.", whaleText: "Tüm standart dikeylerde en düşük oranlar + aylık harcamada %2'ye kadar cashback.",
        tierLabel: { starter: "$250k / ay altı", pro: "$250k – $500k / ay", scale: "$500k – $1M / ay", whale: "$1M – $2.5M / ay", titan: "$2.5M+ / ay" },
        tierDesc: { starter: "Giriş kademesi. Onboard ol, kancalarını bul, funnel'ını doğrula.", pro: "İlk ölçek eşiği. White-hat %3.5'e, gray-hat %6'ya iner.", scale: "Ciddi operatör kademesi. White-hat %3, gray-hat %5, casino %9. Doğrudan platform temsilcisi, değişim önceliği.", whale: "White-hat %2.5, gray-hat %4, casino %8. Ayrıca aylık harcamada %0.5–1 cashback.", titan: "En düşük oranlar: white-hat %2, gray-hat %3, casino %7. Ayrıca aylık harcamada %1–2 cashback." },
        verticals: { whiteHat: "White-hat", grayHat: "Gray-hat", casino: "Casino & Kumar" },
        examples: { whiteHat: "E-ticaret · DTC · SaaS · B2B · Lead Gen · Uygulama", grayHat: "Finans · Kripto · Nutra · Dating · Agresif D2C · Sağlık · Sigorta", casino: "Online casino · Bahis · iGaming (tüm platformlar · lisanslı)" } },

    es: { eyebrow: "Calculadora en vivo", title: "Ve exactamente lo que pagarás.", sub: "Mueve el control, elige tu vertical y mira cómo se desbloquea tu nivel. Sin suscripción — la comisión de recarga de abajo es lo único que pagas.", vertical: "Clase de vertical", spend: "Gasto publicitario mensual", perMo: "/mes", yourTier: "Tu nivel", fee: "Comisión de recarga", monthlyFee: "Comisión mensual", cashback: "Cashback · hasta", net: "Coste mensual neto", whale: "Nivel Whale desbloqueado.", whaleText: "Las tarifas más bajas en todas las verticales estándar, más hasta 2% de cashback sobre el gasto mensual.",
        tierLabel: { starter: "Menos de $250k / mes", pro: "$250k – $500k / mes", scale: "$500k – $1M / mes", whale: "$1M – $2.5M / mes", titan: "$2.5M+ / mes" },
        tierDesc: { starter: "Nivel de entrada. Haz onboarding, encuentra tus ganchos, valida tu funnel.", pro: "Primer umbral de escala. White-hat baja a 3.5%, gray-hat a 6%.", scale: "Nivel de operador serio. White-hat 3%, gray-hat 5%, casino 9%. Representantes directos, prioridad de reemplazo.", whale: "White-hat 2.5%, gray-hat 4%, casino 8%. Más 0.5–1% de cashback sobre el gasto mensual.", titan: "Las tarifas más bajas: white-hat 2%, gray-hat 3%, casino 7%. Más 1–2% de cashback sobre el gasto mensual." },
        verticals: { whiteHat: "White-hat", grayHat: "Gray-hat", casino: "Casino y apuestas" },
        examples: { whiteHat: "eCommerce · DTC · SaaS · B2B · Lead Gen · Apps", grayHat: "Finanzas · Cripto · Nutra · Dating · D2C agresivo · Salud · Seguros", casino: "Casino online · Apuestas · iGaming (todas las plataformas · con licencia)" } },
    pt: { eyebrow: "Calculadora ao vivo", title: "Veja exatamente o que você vai pagar.", sub: "Arraste o controle, escolha sua vertical e veja seu nível ser liberado. Sem assinatura — a taxa de recarga abaixo é a única coisa que você paga.", vertical: "Classe de vertical", spend: "Gasto mensal em anúncios", perMo: "/mês", yourTier: "Seu nível", fee: "Taxa de recarga", monthlyFee: "Taxa mensal", cashback: "Cashback · até", net: "Custo mensal líquido", whale: "Nível Whale liberado.", whaleText: "As menores taxas em todas as verticais padrão, mais hasta 2% de cashback sobre o gasto mensal.",
        tierLabel: { starter: "Menos de $250k / mês", pro: "$250k – $500k / mês", scale: "$500k – $1M / mês", whale: "$1M – $2.5M / mês", titan: "$2.5M+ / mês" },
        tierDesc: { starter: "Nível de entrada. Faça o onboarding, encontre seus ganchos, valide seu funil.", pro: "Primeiro limite de escala. White-hat cai para 3.5%, gray-hat para 6%.", scale: "Nível de operador sério. White-hat 3%, gray-hat 5%, cassino 9%. Representantes diretos, prioridade de substituição.", whale: "White-hat 2.5%, gray-hat 4%, cassino 8%. Mais 0.5–1% de cashback sobre o gasto mensal.", titan: "As taxas mais baixas: white-hat 2%, gray-hat 3%, cassino 7%. Mais 1–2% de cashback sobre o gasto mensal." },
        verticals: { whiteHat: "White-hat", grayHat: "Gray-hat", casino: "Cassino e apostas" },
        examples: { whiteHat: "eCommerce · DTC · SaaS · B2B · Lead Gen · Apps", grayHat: "Finanças · Cripto · Nutra · Dating · D2C agressivo · Saúde · Seguros", casino: "Cassino online · Apostas · iGaming (todas as plataformas · licenciado)" } },
    ru: { eyebrow: "Живой калькулятор", title: "Точно посчитайте, сколько заплатите.", sub: "Двигайте слайдер, выберите вертикаль и смотрите, как открывается ваш уровень. Без подписки — комиссия за пополнение ниже — единственное, что вы платите.", vertical: "Класс вертикали", spend: "Месячный рекламный бюджет", perMo: "/мес", yourTier: "Ваш уровень", fee: "Комиссия за пополнение", monthlyFee: "Комиссия в месяц", cashback: "Кэшбэк · до", net: "Чистая стоимость в месяц", whale: "Уровень Whale открыт.", whaleText: "Минимальные ставки на всех стандартных вертикалях плюс кэшбэк до 2% с месячного расхода.",
        tierLabel: { starter: "До $250k / мес", pro: "$250k – $500k / мес", scale: "$500k – $1M / мес", whale: "$1M – $2.5M / мес", titan: "$2.5M+ / мес" },
        tierDesc: { starter: "Стартовый уровень. Пройдите онбординг, найдите хуки, проверьте воронку.", pro: "Первый порог масштаба. White-hat снижается до 3.5%, gray-hat — до 6%.", scale: "Уровень серьёзного оператора. White-hat 3%, gray-hat 5%, казино 9%. Прямые менеджеры платформ, приоритет замены.", whale: "White-hat 2.5%, gray-hat 4%, казино 8%. Плюс кэшбэк 0.5–1% с месячного расхода.", titan: "Самые низкие ставки: white-hat 2%, gray-hat 3%, казино 7%. Плюс кэшбэк 1–2% с месячного расхода." },
        verticals: { whiteHat: "White-hat", grayHat: "Gray-hat", casino: "Казино и гэмблинг" },
        examples: { whiteHat: "eCommerce · DTC · SaaS · B2B · Lead Gen · Приложения", grayHat: "Финансы · Крипто · Нутра · Дейтинг · Агрессивный D2C · Здоровье · Страхование", casino: "Онлайн-казино · Беттинг · iGaming (все платформы · с лицензией)" } },
};

const SPEND_PRESETS = [
    { label: "$50k", value: 50_000 },
    { label: "$150k", value: 150_000 },
    { label: "$350k", value: 350_000 },
    { label: "$750k", value: 750_000 },
];

function formatUsd(n) {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 10_000) return `$${Math.round(n / 1_000)}k`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${Math.round(n)}`;
}

export default function VolumeCalculator() {
    const { language } = useLanguage();
    const t = I18N[language] || I18N.en;
    const [spend, setSpend] = useState(150_000);
    const [vertical, setVertical] = useState("grayHat");

    const result = useMemo(
        () => calculateMonthly(spend, vertical),
        [spend, vertical],
    );

    const verticalLabel = t.verticals?.[vertical] || VERTICAL_CLASSES.find((v) => v.id === vertical)?.name;

    return (
        <div
            data-testid="volume-calculator"
            className="bg-card border border-white/[0.08] rounded-3xl p-6 md:p-10 relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-primary/[0.04] blur-[120px] pointer-events-none"
            />

            <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Inputs */}
                <div className="lg:col-span-7 space-y-7">
                    <div>
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-2">
                            {t.eyebrow}
                        </p>
                        <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                            {t.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 max-w-md">
                            {t.sub}
                        </p>
                    </div>

                    {/* Vertical tabs */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                            {t.vertical}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {VERTICAL_CLASSES.map((v) => (
                                <button
                                    key={v.id}
                                    onClick={() => setVertical(v.id)}
                                    data-testid={`calc-vertical-${v.id}`}
                                    className={`px-3 py-3 rounded-xl border text-left transition-all ${
                                        vertical === v.id
                                            ? "border-primary/50 bg-primary/[0.06]"
                                            : "border-white/[0.08] bg-background/50 hover:border-white/15"
                                    }`}
                                >
                                    <p
                                        className={`text-sm font-semibold ${
                                            vertical === v.id
                                                ? "text-primary"
                                                : "text-foreground"
                                        }`}
                                    >
                                        {t.verticals?.[v.id] || v.name}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground mt-1 leading-tight line-clamp-2">
                                        {t.examples?.[v.id] || v.examples}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Spend slider */}
                    <div className="space-y-4">
                        <div className="flex items-baseline justify-between">
                            <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                                {t.spend}
                            </label>
                            <span className="font-display font-black text-3xl md:text-4xl text-foreground tabular-nums">
                                {formatUsd(spend)}
                            </span>
                        </div>

                        <Slider
                            data-testid="calc-spend-slider"
                            value={[spend]}
                            onValueChange={(v) => setSpend(v[0])}
                            min={5_000}
                            max={3_000_000}
                            step={10_000}
                            className="py-2"
                        />

                        <div className="flex flex-wrap gap-2 pt-1">
                            {SPEND_PRESETS.map((p) => (
                                <button
                                    key={p.value}
                                    onClick={() => setSpend(p.value)}
                                    data-testid={`calc-preset-${p.value}`}
                                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                                        spend === p.value
                                            ? "border-primary/50 bg-primary/10 text-primary"
                                            : "border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground"
                                    }`}
                                >
                                    {p.label}{t.perMo}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Output */}
                <div className="lg:col-span-5 space-y-3">
                    <div
                        data-testid="calc-tier-card"
                        className="rounded-2xl border-2 p-5 transition-colors"
                        style={{
                            borderColor: `${result.tier.accent}55`,
                            background: `${result.tier.accent}08`,
                        }}
                    >
                        <div className="flex items-baseline justify-between mb-1">
                            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                                {t.yourTier}
                            </p>
                            <p
                                className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold"
                                style={{ color: result.tier.accent }}
                            >
                                {t.tierLabel?.[result.tier.id] || result.tier.label}
                            </p>
                        </div>
                        <h4
                            className="font-display font-black text-3xl mb-1"
                            style={{ color: result.tier.accent }}
                        >
                            {result.tier.name}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                            {t.tierDesc?.[result.tier.id] || result.tier.description}
                        </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-background/50 p-5 space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground inline-flex items-center gap-2">
                                <TrendingDown className="w-3.5 h-3.5 text-primary" />
                                {t.fee} · {verticalLabel}
                            </span>
                            <span className="font-mono font-semibold text-foreground tabular-nums">
                                {result.feePct}%
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                                {t.monthlyFee}
                            </span>
                            <span className="font-display font-bold text-2xl text-foreground tabular-nums">
                                {formatUsd(result.fee)}
                            </span>
                        </div>

                        {result.cashbackPct > 0 && (
                            <div
                                data-testid="calc-cashback"
                                className="flex items-center justify-between text-sm pt-3 border-t border-white/5"
                            >
                                <span className="text-[#2FA57C] inline-flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5" />
                                    {t.cashback} {result.cashbackPct}%
                                </span>
                                <span className="font-display font-bold text-2xl text-[#2FA57C] tabular-nums">
                                    +{formatUsd(result.cashback)}
                                </span>
                            </div>
                        )}

                        <div
                            className="flex items-center justify-between pt-3 border-t border-white/10"
                            data-testid="calc-net-cost"
                        >
                            <span className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2">
                                <Wallet className="w-3.5 h-3.5" />
                                {t.net}
                            </span>
                            <span
                                className={`font-display font-black text-2xl tabular-nums ${
                                    result.netCost <= 0
                                        ? "text-[#00b67a]"
                                        : "text-foreground"
                                }`}
                            >
                                {result.netCost < 0 ? "-" : ""}
                                {formatUsd(Math.abs(result.netCost))}
                            </span>
                        </div>
                    </div>

                    {result.tier.cashback && (
                        <div className="rounded-2xl border border-[#2FA57C]/30 bg-[#2FA57C]/[0.04] p-4 text-xs text-foreground/85">
                            <span className="text-[#2FA57C] font-semibold">
                                {t.whale}
                            </span>{" "}
                            {t.whaleText}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
